import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Zap, UserPlus, CalendarPlus, FileText, Receipt,
  Users, Car, Sparkles, TrendingUp, ArrowUpRight, AlertTriangle, Mail, X, Phone,
} from "lucide-react";
import { toast } from "sonner";
import { CRM_API, errMsg } from "./api";
import { useCrmAuth } from "./CrmAuth";
import { isNewLead, markItemSeen } from "./leadSeen";
import PageHeader from "./components/PageHeader";
import { AppointmentModal } from "./Calendar";

const QUICK = [
  { to: "/crm/check-in", label: "Check-In Vehicle", icon: Zap, color: "#1F5EFF", primary: true, testid: "quick-checkin" },
  { to: "/crm/appointments/new", label: "New Appointment", icon: CalendarPlus, testid: "quick-appointment" },
  { to: "/crm/customers/new", label: "New Customer", icon: UserPlus, testid: "quick-customer" },
  { to: "/crm/estimates/new", label: "Create Estimate", icon: FileText, testid: "quick-estimate" },
  { to: "/crm/invoices/new", label: "Create Invoice", icon: Receipt, testid: "quick-invoice" },
];

export default function Dashboard() {
  const { user } = useCrmAuth();
  const [data, setData] = useState(null);
  const [ops, setOps] = useState(null);
  const [err, setErr] = useState(null);
  const [webBookings, setWebBookings] = useState([]);
  const [webLeads, setWebLeads] = useState([]);
  const [lowStock, setLowStock] = useState([]);
  const [quoteByAppt, setQuoteByAppt] = useState({});
  const [replyTo, setReplyTo] = useState(null); // {id, name, email} of the quote being answered
  const [leadDetail, setLeadDetail] = useState(null); // full quote object shown in the detail modal
  const [bookPrefill, setBookPrefill] = useState(null); // {quote, prefill} — quote → appointment conversion

  // Open the appointment modal prefilled from a website quote: customer matched
  // by phone (or linked), requested slot, estimated price and service names.
  const openBookingFromQuote = async (q) => {
    let customer_id = q.customer_id || "";
    if (!customer_id && q.phone) {
      try {
        const digits = (s) => (s || "").replace(/\D/g, "");
        const { data } = await CRM_API.get(`/customers?q=${encodeURIComponent(q.phone.trim())}&limit=10`);
        const m = (Array.isArray(data) ? data : []).find((c) => digits(c.phone) && digits(c.phone) === digits(q.phone));
        if (m) customer_id = m.id;
      } catch { /* picker stays empty — staff creates the customer inline */ }
    }
    const snap = q.pricing_snapshot || {};
    const serviceNames = (snap.services || []).map((s) => s.name).filter(Boolean);
    if (!serviceNames.length && q.service) serviceNames.push(...q.service.split("+").map((s) => s.trim()).filter(Boolean));
    setBookPrefill({
      quote: q,
      prefill: {
        customer_id,
        scheduled_at: q.preferred_date ? `${q.preferred_date}T${q.preferred_time || "10:00"}:00` : undefined,
        estimated_price: q.estimated_price || snap.total || 0,
        notes: `Website request ${q.id}${q.service ? ` — ${q.service}` : ""}`,
        service_names: serviceNames,
      },
    });
  };

  const setLeadStatus = async (q, status) => {
    try {
      await CRM_API.patch(`/quotes/${q.id}`, { status });
      setWebLeads((prev) => ["done", "cancelled"].includes(status)
        ? prev.filter((x) => x.id !== q.id)
        : prev.map((x) => (x.id === q.id ? { ...x, status } : x)));
      setLeadDetail((prev) => (prev && prev.id === q.id ? { ...prev, status } : prev));
      if (status === "done") setLeadDetail(null);
      toast.success(status === "done" ? "Request done — removed from the list"
        : status === "in_progress" ? "Request marked in progress" : "Status updated");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const deleteLead = async (q) => {
    try {
      await CRM_API.delete(`/quotes/${q.id}`);
      setWebLeads((prev) => prev.filter((x) => x.id !== q.id));
      setLeadDetail((prev) => (prev && prev.id === q.id ? null : prev));
      toast.success("Request deleted");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  useEffect(() => {
    CRM_API.get("/dashboard").then(({ data }) => setData(data)).catch((e) => setErr(e?.message));    CRM_API.get("/dashboard/ops").then(({ data }) => setOps(data)).catch(() => {});
    CRM_API.get("/inventory/items", { params: { low: 1 } })
      .then(({ data }) => setLowStock(Array.isArray(data) ? data : []))
      .catch(() => {});
    // Website activity: online bookings + quote leads (so nothing arrives "nowhere")
    // Latest first by created_at — no date window, so past-dated bookings are visible too
    CRM_API.get("/appointments").then(({ data }) => {
      setWebBookings((Array.isArray(data) ? data : [])
        .filter((a) => a.created_by === "website" && !["cancelled", "no_show", "completed"].includes(a.status))
        .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""))
        .slice(0, 6));
    }).catch(() => {});
    CRM_API.get("/quotes").then(({ data }) => {
      const all = Array.isArray(data) ? data : [];
      const map = {};
      for (const q of all) if (q.appointment_id) map[q.appointment_id] = q;
      setQuoteByAppt(map);
      const cutoff = Date.now() - 14 * 864e5;
      setWebLeads(all
        .filter((q) => !q.appointment_id && !["done", "cancelled"].includes(q.status)
          && new Date(q.created_at).getTime() > cutoff)
        .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""))
        .slice(0, 6));
    }).catch(() => {});
  }, []);

  const now = new Date();
  const greet = now.getHours() < 12 ? "Good morning" : now.getHours() < 18 ? "Good afternoon" : "Good evening";

  return (
    <div data-testid="crm-dashboard">
      <PageHeader
        eyebrow={now.toLocaleDateString("en-CA", { weekday: "long", month: "long", day: "numeric" })}
        title={`${greet}, ${user?.name?.split(" ")[0] || "team"}.`}
        subtitle="Overview of what's happening in the studio today."
      />

      <div className="px-6 md:px-10 pb-16">
        {/* Quick actions */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 md:gap-4 mb-10">
          {QUICK.map((q) => (
            <Link
              key={q.to} to={q.to} data-testid={q.testid}
              className={`group relative flex items-center gap-3 px-4 md:px-5 py-4 md:py-5 rounded-xl border transition-all ${
                q.primary
                  ? "bg-[#1F5EFF] border-[#1F5EFF] hover:bg-white hover:text-black text-white"
                  : "bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/25 text-white"
              }`}
            >
              <q.icon className="w-5 h-5" strokeWidth={1.75} />
              <span className="text-[13.5px] font-medium leading-tight">{q.label}</span>
              <ArrowUpRight className="w-4 h-4 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
            </Link>
          ))}
        </div>

        {err && <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-300 text-sm mb-6">{err}</div>}

        {/* Customer follow-ups — internal retention reminders (never auto-sent) */}
        {data?.followups && (
          <Link to="/crm/follow-ups" data-testid="followups-block"
            className="block mb-10 p-5 rounded-xl bg-emerald-500/5 border border-emerald-500/25 hover:border-emerald-400/50 transition-colors">
            <div className="flex items-center gap-3 mb-3">
              <span className="w-6 h-px bg-emerald-400" />
              <span className="font-mono-alt text-[10px] tracking-widest text-emerald-300 uppercase">Customer follow-ups</span>
              <span className="ml-auto text-emerald-300/70 text-xs">Open list →</span>
            </div>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-1.5 text-sm">
              <span className={data.followups.overdue > 0 ? "text-red-300" : "text-white/50"} data-testid="followups-overdue-count">
                Overdue <span className="font-semibold tabular-nums">{data.followups.overdue}</span>
              </span>
              <span className={data.followups.due_today > 0 ? "text-amber-300" : "text-white/50"} data-testid="followups-due-count">
                Due today <span className="font-semibold tabular-nums">{data.followups.due_today}</span>
              </span>
              <span className="text-white/50" data-testid="followups-upcoming-count">
                Upcoming <span className="font-semibold tabular-nums">{data.followups.upcoming}</span>
              </span>
              {(data.followups.by_suggested || []).slice(0, 3).map((g) => (
                <span key={g.key} className="text-white/40 text-xs" data-testid={`followups-group-${g.key}`}>
                  {g.count} × {g.name}
                </span>
              ))}
            </div>
          </Link>
        )}

        {/* Website activity — new requests highlighted until opened, then move to Earlier */}
        <div className="mb-10 p-5 rounded-xl bg-[#1F5EFF]/8 border border-[#1F5EFF]/30" data-testid="website-activity">
          <div className="flex items-center gap-3 mb-4">
            <span className="w-6 h-px bg-[#1F5EFF]" />
            <span className="font-mono-alt text-[10px] tracking-widest text-[#8db2ff] uppercase">From the website</span>
            {(webBookings.some(isNewLead) || webLeads.some(isNewLead)) && (
              <span data-testid="website-activity-new-flag"
                className="ml-auto font-mono-alt text-[10px] tracking-widest text-[#8db2ff] uppercase animate-pulse">
                ● New requests
              </span>
            )}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <WebColumn
              title={`Online bookings (${webBookings.length})`}
              items={webBookings}
              emptyText="None yet — new online bookings appear here automatically"
              renderLabel={(a) => a.pricing_snapshot?.service_name || "Online booking"}
              renderMeta={(a) => `${(a.scheduled_at || "").slice(0, 10)} ${(a.scheduled_at || "").slice(11, 16)}`}
              to="/crm/appointments"
              kind="booking"
              getReplyQuote={(a) => quoteByAppt[a.id]}
              onReply={setReplyTo}
            />
            <WebColumn
              title={`Quote requests, last 14 days (${webLeads.length})`}
              items={webLeads}
              emptyText="None yet — new quote requests appear here automatically"
              renderLabel={(q) => `${q.name} · ${q.service || "Quote"}`}
              renderMeta={(q) => (q.created_at || "").slice(0, 10)}
              to="/crm/leads"
              kind="lead"
              getReplyQuote={(q) => q}
              onReply={setReplyTo}
              onOpen={setLeadDetail}
            />
          </div>
        </div>

        {replyTo && <ReplyModal quote={replyTo} onClose={() => setReplyTo(null)} />}
        {leadDetail && (
          <LeadDetailModal
            quote={leadDetail}
            onClose={() => setLeadDetail(null)}
            onReply={(q) => { setLeadDetail(null); setReplyTo(q); }}
            onSetStatus={(status) => setLeadStatus(leadDetail, status)}
            onBook={openBookingFromQuote}
            onDelete={deleteLead}
          />
        )}
        {bookPrefill && (
          <AppointmentModal
            prefill={bookPrefill.prefill}
            onClose={() => setBookPrefill(null)}
            onSaved={async (apptId) => {
              const q = bookPrefill.quote;
              setBookPrefill(null);
              if (apptId) {
                try {
                  await CRM_API.patch(`/quotes/${q.id}`, { status: "in_progress", appointment_id: apptId });
                  setWebLeads((prev) => prev.filter((x) => x.id !== q.id));
                  setLeadDetail((prev) => (prev && prev.id === q.id ? { ...prev, status: "in_progress", appointment_id: apptId } : prev));
                  toast.success("Quote linked to the new appointment");
                } catch { /* appointment saved; link failed silently */ }
              }
            }}
          />
        )}

        {/* Low stock alerts — chemicals/materials at or below min threshold */}
        {lowStock.length > 0 && (
          <div className="mb-10 p-5 rounded-xl bg-amber-500/8 border border-amber-500/30" data-testid="low-stock-alerts">
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle className="w-4 h-4 text-amber-400" strokeWidth={1.75} />
              <span className="font-mono-alt text-[10px] tracking-widest text-amber-300 uppercase">Low stock alerts</span>
              <Link to="/crm/inventory" data-testid="low-stock-view-all"
                className="ml-auto text-amber-300/80 text-xs hover:underline">Open inventory →</Link>
            </div>
            <div className="space-y-1.5">
              {lowStock.map((it) => (
                <Link key={it.id} to="/crm/inventory" data-testid={`low-stock-item-${it.id}`}
                  className="flex items-center justify-between gap-3 p-2.5 rounded-lg bg-white/5 border border-white/10 hover:border-amber-400/50 text-sm">
                  <span className="text-white truncate">{it.name}{it.brand ? ` · ${it.brand}` : ""}</span>
                  <span className={`font-mono-alt text-[11px] whitespace-nowrap ${it.status === "out" ? "text-red-400" : "text-amber-300"}`}>
                    {it.status === "out"
                      ? "OUT OF STOCK"
                      : `${it.quantity} ${it.unit} left${it.min_quantity ? ` (min ${it.min_quantity})` : ""}`}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Ops KPIs */}
        <SectionLabel>Studio floor</SectionLabel>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4 mb-10">
          <Kpi label="Appointments today" value={ops?.appointments_today ?? data?.counts?.appointments_today ?? 0} accent testid="kpi-appt-today" />
          <Kpi label="Vehicles in shop" value={ops?.vehicles_in_shop ?? 0} testid="kpi-in-shop" />
          <Kpi label="In progress" value={ops?.in_progress ?? 0} testid="kpi-in-progress" />
          <Kpi label="Ready for pickup" value={ops?.ready_for_pickup ?? 0} testid="kpi-ready" />
          <Kpi label="Awaiting approval" value={ops?.waiting_approval ?? 0} testid="kpi-waiting" />
        </div>

        {/* Revenue */}
        <SectionLabel>Revenue</SectionLabel>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 mb-10">
          <RevCard label="Revenue today" value={ops?.revenue_today ?? 0} testid="rev-today" />
          <RevCard label="Revenue this week" value={ops?.revenue_week ?? 0} testid="rev-week" />
          <RevCard label="Outstanding invoices" value={ops?.outstanding_invoices ?? 0} money={false} testid="rev-outstanding" />
        </div>

        {/* Directory KPIs */}
        <SectionLabel>Directory</SectionLabel>
        <div className="grid grid-cols-3 gap-3 md:gap-4 mb-10">
          <Kpi label="Customers" value={data?.counts?.customers ?? 0} icon={Users} testid="kpi-customers" />
          <Kpi label="Vehicles" value={data?.counts?.vehicles ?? 0} icon={Car} testid="kpi-vehicles" />
          <Kpi label="Active services" value={data?.counts?.services_active ?? 0} icon={Sparkles} testid="kpi-services" />
        </div>

        {/* Recent lists */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
          <RecentPanel title="Recent customers" testid="recent-customers">
            {(data?.recent_customers || []).length === 0 ? (
              <EmptyRow label="No customers yet." linkTo="/crm/customers/new" linkLabel="Add customer" />
            ) : (
              (data?.recent_customers || []).map((c) => (
                <Link key={c.id} to={`/crm/customers/${c.id}`} className="flex items-center gap-3 px-4 py-3 hover:bg-white/5 rounded-lg">
                  <div className="w-9 h-9 rounded-full bg-white/8 flex items-center justify-center text-white/70 text-xs">
                    {(c.first_name || "?").slice(0, 1)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-white text-sm truncate">{c.first_name} {c.last_name}</div>
                    <div className="text-white/40 text-xs truncate">{c.phone}</div>
                  </div>
                  <ArrowUpRight className="w-4 h-4 text-white/30" />
                </Link>
              ))
            )}
          </RecentPanel>
          <RecentPanel title="Recent vehicles" testid="recent-vehicles">
            {(data?.recent_vehicles || []).length === 0 ? (
              <EmptyRow label="No vehicles yet." linkTo="/crm/vehicles/new" linkLabel="Add vehicle" />
            ) : (
              (data?.recent_vehicles || []).map((v) => (
                <Link key={v.id} to={`/crm/vehicles/${v.id}`} className="flex items-center gap-3 px-4 py-3 hover:bg-white/5 rounded-lg">
                  <div className="w-9 h-9 rounded-lg bg-white/8 flex items-center justify-center text-white/70">
                    <Car className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-white text-sm truncate">
                      {[v.year, v.make, v.model].filter(Boolean).join(" ")}
                    </div>
                    <div className="text-white/40 text-xs truncate font-mono-alt">
                      {[v.vin, v.license_plate].filter(Boolean).join(" · ") || "—"}
                    </div>
                  </div>
                  <ArrowUpRight className="w-4 h-4 text-white/30" />
                </Link>
              ))
            )}
          </RecentPanel>
        </div>
      </div>
    </div>
  );
}

const SectionLabel = ({ children }) => (
  <div className="flex items-center gap-3 mb-4">
    <span className="w-6 h-px bg-[#1F5EFF]" />
    <span className="font-mono-alt text-[10px] tracking-widest text-white/50">{children}</span>
  </div>
);

const Kpi = ({ label, value, accent, icon: Icon, testid }) => (
  <div
    data-testid={testid}
    className={`p-4 md:p-5 rounded-xl border ${accent ? "bg-[#1F5EFF]/10 border-[#1F5EFF]/30" : "bg-white/5 border-white/10"}`}
  >
    <div className="flex items-center justify-between mb-2">
      <div className="text-[10px] font-mono-alt uppercase tracking-widest text-white/50">{label}</div>
      {Icon && <Icon className="w-3.5 h-3.5 text-white/30" />}
    </div>
    <div className={`font-display text-3xl md:text-4xl tabular-nums ${accent ? "text-[#8db2ff]" : "text-white"}`}>
      {value}
    </div>
  </div>
);

const RevCard = ({ label, value, money = true, testid }) => (
  <div data-testid={testid} className="p-5 rounded-xl bg-white/5 border border-white/10">
    <div className="flex items-center justify-between mb-2">
      <div className="text-[10px] font-mono-alt uppercase tracking-widest text-white/50">{label}</div>
      <TrendingUp className="w-3.5 h-3.5 text-white/30" />
    </div>
    <div className="font-display text-3xl md:text-4xl text-white tabular-nums">
      {money ? `$${(Number(value) || 0).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : (value ?? 0)}
    </div>
  </div>
);

const RecentPanel = ({ title, children, testid }) => (
  <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden" data-testid={testid}>
    <div className="px-5 py-3 border-b border-white/10 flex items-center justify-between">
      <div className="font-display text-sm text-white">{title}</div>
    </div>
    <div className="p-2">{children}</div>
  </div>
);

const EmptyRow = ({ label, linkTo, linkLabel }) => (
  <div className="px-4 py-8 text-center">
    <div className="text-white/40 text-sm mb-3">{label}</div>
    <Link to={linkTo} className="text-[#1F5EFF] text-sm hover:underline">{linkLabel} →</Link>
  </div>
);

// Website-activity column: NEW items highlighted on top, earlier ones dimmed below.
// Opening an item marks it seen — it moves to "Earlier" on next render.
const WebColumn = ({ title, items, emptyText, renderLabel, renderMeta, to, kind, getReplyQuote, onReply, onOpen }) => {
  const fresh = items.filter(isNewLead);
  const earlier = items.filter((it) => !isNewLead(it));
  const Row = ({ it, isNew }) => {
    const rq = getReplyQuote ? getReplyQuote(it) : null;
    const inner = (
      <>
        <span className={`truncate flex items-center gap-2 ${isNew ? "text-white" : "text-white/70"}`}>
          {isNew && (
            <span className="shrink-0 px-1.5 py-0.5 rounded bg-[#1F5EFF] text-white text-[9px] font-bold tracking-wider">NEW</span>
          )}
          {renderLabel(it)}
        </span>
        <span className={`font-mono-alt text-[10px] whitespace-nowrap ${isNew ? "text-[#8db2ff]" : "text-white/40"}`}>
          {renderMeta(it)}
        </span>
      </>
    );
    return (
      <div
        data-testid={`web-${kind}-${isNew ? "new" : "old"}-${it.id}`}
        className={`flex items-center gap-2 p-2.5 rounded-lg border text-sm transition-colors ${
          isNew
            ? "bg-[#1F5EFF]/15 border-[#1F5EFF]/60 hover:border-[#1F5EFF]"
            : "bg-white/5 border-white/10 hover:border-[#1F5EFF]/50"
        }`}
      >
        {onOpen ? (
          <button onClick={() => { markItemSeen(it.id); onOpen(it); }}
            data-testid={`web-${kind}-open-${it.id}`}
            className="flex-1 min-w-0 flex items-center justify-between gap-3 text-left">
            {inner}
          </button>
        ) : (
          <Link to={to} onClick={() => markItemSeen(it.id)} className="flex-1 min-w-0 flex items-center justify-between gap-3">
            {inner}
          </Link>
        )}
        {rq?.email && (
          <button
            data-testid={`web-${kind}-reply-${it.id}`}
            onClick={() => onReply(rq)}
            title={`Reply to ${rq.email}`}
            className="shrink-0 p-1.5 rounded-lg text-white/50 hover:text-white hover:bg-[#1F5EFF]/30 transition-colors"
          >
            <Mail className="w-4 h-4" strokeWidth={1.75} />
          </button>
        )}
      </div>
    );
  };
  return (
    <div>
      <div className="text-white/50 text-xs mb-2">{title}</div>
      {items.length === 0 && <div className="text-white/30 text-xs">{emptyText}</div>}
      {fresh.length > 0 && (
        <div className="mb-3">
          <div className="font-mono-alt text-[9px] tracking-widest text-[#8db2ff] uppercase mb-1.5">New ({fresh.length})</div>
          <div className="space-y-1.5">{fresh.map((it) => <Row key={it.id} it={it} isNew />)}</div>
        </div>
      )}
      {earlier.length > 0 && (
        <div className={fresh.length > 0 ? "opacity-60" : ""}>
          {fresh.length > 0 && (
            <div className="font-mono-alt text-[9px] tracking-widest text-white/40 uppercase mb-1.5">Earlier ({earlier.length})</div>
          )}
          <div className="space-y-1.5">{earlier.map((it) => <Row key={it.id} it={it} isNew={false} />)}</div>
        </div>
      )}
    </div>
  );
};

const REPLY_TEMPLATES = [
  {
    key: "confirm",
    label: "Confirm booking",
    text: "Hi {name},\n\nGreat news — your appointment at WTD Detailing & Tint is confirmed!\n\nIf anything changes, just reply to this email or call us at +1 (368) 380-8483.\n\nSee you soon,\nWTD Detailing & Tint",
  },
  {
    key: "photos",
    label: "Request photos",
    text: "Hi {name},\n\nThanks for reaching out! To give you an exact quote, could you please reply to this email with a few photos of your vehicle (the areas you'd like us to look at)?\n\nWe'll get back to you with a firm price right away.\n\nWTD Detailing & Tint",
  },
  {
    key: "reschedule",
    label: "Reschedule",
    text: "Hi {name},\n\nUnfortunately we need to move your appointment. Could you let us know which day and time works best for you?\n\nSorry for the inconvenience — we'll make sure your vehicle gets the full WTD treatment.\n\nWTD Detailing & Tint",
  },
];

// Reply-by-email modal: templates, CRM service picker, branded send.
export const ReplyModal = ({ quote, onClose }) => {
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [services, setServices] = useState([]);
  const [showServices, setShowServices] = useState(false);
  const [picked, setPicked] = useState(new Set());

  useEffect(() => {
    CRM_API.get("/services")
      .then(({ data }) => setServices((Array.isArray(data) ? data : []).filter((s) => s.active !== false)))
      .catch(() => {});
  }, []);

  const applyTemplate = (tpl) => {
    const firstName = (quote.name || "there").trim().split(/\s+/)[0];
    setMessage(tpl.text.replaceAll("{name}", firstName));
  };

  const togglePick = (id) => {
    setPicked((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const insertServices = () => {
    const chosen = services.filter((s) => picked.has(s.id));
    if (!chosen.length) return;
    const lines = chosen.map((s) => `• ${s.name} — $${Number(s.price || 0).toFixed(0)}`);
    const block = `\n\nHere's what we can do for your vehicle:\n${lines.join("\n")}\n\nJust let us know which ones you'd like, and we'll confirm the exact total.`;
    setMessage((m) => (m.trim() ? m.trimEnd() + block : block.trimStart()));
    setPicked(new Set());
    setShowServices(false);
  };

  const send = async () => {
    if (!message.trim()) { toast.error("Write a message first"); return; }
    setSending(true);
    try {
      const { data } = await CRM_API.post(`/quotes/${quote.id}/reply`, { message: message.trim() });
      if (data?.ok) {
        toast.success(`Reply sent to ${quote.email}`);
        markItemSeen(quote.id);
        onClose();
      } else if (data?.skipped) {
        toast.error(data.detail || "Email is not configured yet");
      } else {
        toast.error("Reply was not sent");
      }
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      data-testid="reply-modal" onClick={onClose}>
      <div className="w-full max-w-lg rounded-2xl bg-[#121216] border border-white/10 p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="font-display text-lg text-white">Reply to {quote.name || "customer"}</div>
            <div className="text-white/40 text-xs mt-1 font-mono-alt" data-testid="reply-modal-to">{quote.email}</div>
          </div>
          <button data-testid="reply-modal-close" onClick={onClose}
            className="p-1.5 text-white/50 hover:text-white rounded-lg hover:bg-white/5">
            <X className="w-4 h-4" />
          </button>
        </div>
        {quote.service && (
          <div className="text-white/50 text-xs mb-3">Re: {quote.service}{quote.vehicle ? ` — ${quote.vehicle}` : ""}</div>
        )}

        {/* Previous messages for this request, newest first */}
        {Array.isArray(quote.communications) && quote.communications.length > 0 && (
          <div className="mb-3 max-h-28 overflow-y-auto space-y-1.5 pr-1" data-testid="reply-history">
            {[...quote.communications].reverse().map((m, i) => (
              <div key={i} className="text-[11px] px-2.5 py-1.5 rounded-lg bg-white/5 border border-white/8 flex items-center gap-2">
                <span className={`shrink-0 px-1 py-px rounded text-[8px] font-bold tracking-wider ${m.ok ? "bg-emerald-500/15 text-emerald-300" : "bg-red-500/15 text-red-300"}`}>
                  {m.ok ? "SENT" : "FAIL"}
                </span>
                <span className="text-white/60 whitespace-nowrap">{(m.at || "").slice(0, 16).replace("T", " ")}</span>
                <span className="text-white/40 truncate">{m.kind === "auto_ack" ? "Auto-acknowledgement" : (m.subject || m.body)}</span>
              </div>
            ))}
          </div>
        )}

        {/* One-click templates */}
        <div className="flex flex-wrap gap-1.5 mb-3">
          {REPLY_TEMPLATES.map((t) => (
            <button key={t.key} data-testid={`reply-template-${t.key}`} onClick={() => applyTemplate(t)}
              className="px-2.5 py-1 rounded-full text-[11px] border border-white/15 text-white/60 hover:text-white hover:border-[#1F5EFF]/60 hover:bg-[#1F5EFF]/15 transition-colors">
              {t.label}
            </button>
          ))}
          <button data-testid="reply-toggle-services" onClick={() => setShowServices((v) => !v)}
            className={`px-2.5 py-1 rounded-full text-[11px] border transition-colors ${
              showServices ? "border-[#1F5EFF] text-white bg-[#1F5EFF]/20" : "border-white/15 text-white/60 hover:text-white hover:border-[#1F5EFF]/60"
            }`}>
            + Insert services
          </button>
        </div>

        {/* CRM service picker */}
        {showServices && (
          <div className="mb-3 rounded-xl border border-[#1F5EFF]/30 bg-[#1F5EFF]/5 p-3" data-testid="reply-services-panel">
            <div className="max-h-40 overflow-y-auto space-y-1">
              {services.map((s) => (
                <label key={s.id} data-testid={`reply-service-${s.id}`}
                  className="flex items-center gap-2.5 px-2 py-1.5 rounded-lg hover:bg-white/5 cursor-pointer text-sm">
                  <input type="checkbox" checked={picked.has(s.id)} onChange={() => togglePick(s.id)}
                    className="accent-[#1F5EFF]" />
                  <span className="text-white/80 flex-1 truncate">{s.name}</span>
                  <span className="text-white/40 font-mono-alt text-[11px]">${Number(s.price || 0).toFixed(0)}</span>
                </label>
              ))}
              {services.length === 0 && <div className="text-white/30 text-xs p-2">Loading services…</div>}
            </div>
            <button data-testid="reply-insert-services" onClick={insertServices} disabled={picked.size === 0}
              className="mt-2 w-full py-1.5 rounded-lg text-xs font-medium bg-[#1F5EFF] text-white disabled:opacity-40 hover:bg-[#8db2ff] hover:text-black transition-colors">
              Insert selected ({picked.size})
            </button>
          </div>
        )}

        <textarea
          data-testid="reply-modal-message"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={7}
          autoFocus
          placeholder="Write your reply or pick a template above… The customer gets a branded WTD email, replies go to hello@wtddetailing.ca"
          className="w-full rounded-xl bg-white/5 border border-white/10 focus:border-[#1F5EFF]/60 outline-none p-3 text-sm text-white placeholder:text-white/30 resize-y"
        />
        <div className="flex justify-end gap-2 mt-4">
          <button data-testid="reply-modal-cancel" onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm text-white/60 hover:text-white hover:bg-white/5">
            Cancel
          </button>
          <button data-testid="reply-modal-send" onClick={send} disabled={sending}
            className="px-5 py-2 rounded-lg text-sm font-medium bg-[#1F5EFF] hover:bg-[#8db2ff] hover:text-black text-white disabled:opacity-50 transition-colors">
            {sending ? "Sending…" : "Send reply"}
          </button>
        </div>
      </div>
    </div>
  );
};


// Full website-request card: everything the client sent + history + actions.
export const LeadDetailModal = ({ quote, onClose, onReply, onSetStatus, onBook, onDelete }) => {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const rows = [
    ["Phone", quote.phone],
    ["Email", quote.email],
    ["Vehicle", quote.vehicle],
    ["Service", quote.service],
    quote.booking_date ? ["Requested date", String(quote.booking_date).slice(0, 16).replace("T", " ")] : null,
    quote.preferred_date ? ["Wants", `${quote.preferred_date}${quote.preferred_time ? " at " + quote.preferred_time : ""} (to confirm)`] : null,
    quote.estimated_price ? ["Estimated", `$${Number(quote.estimated_price).toFixed(2)}`] : null,
    ["Submitted", (quote.created_at || "").slice(0, 16).replace("T", " ")],
    ["Status", quote.status],
  ].filter((r) => r && r[1]);
  const addons = (quote.pricing_snapshot?.addons || []).map((a) => a.label).filter(Boolean);
  const comms = Array.isArray(quote.communications) ? [...quote.communications].reverse() : [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      data-testid="lead-detail-modal" onClick={onClose}>
      <div className="w-full max-w-lg rounded-2xl bg-[#121216] border border-white/10 p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-5">
          <div>
            <div className="font-display text-lg text-white" data-testid="lead-detail-name">{quote.name || "Website request"}</div>
            <div className="text-white/40 text-xs mt-1">{quote.service || "Quote request"}{quote.vehicle ? ` — ${quote.vehicle}` : ""}</div>
          </div>
          <button data-testid="lead-detail-close" onClick={onClose}
            className="p-1.5 text-white/50 hover:text-white rounded-lg hover:bg-white/5">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-2 mb-4">
          {rows.map(([k, v]) => (
            <div key={k} className="p-2.5 rounded-lg bg-white/5 border border-white/8">
              <div className="text-[9px] font-mono-alt tracking-widest text-white/40 uppercase">{k}</div>
              <div className="text-white text-sm mt-0.5 break-all" data-testid={`lead-detail-${k.toLowerCase().replace(/\s+/g, "-")}`}>{v}</div>
            </div>
          ))}
        </div>

        {addons.length > 0 && (
          <div className="mb-4">
            <div className="text-[9px] font-mono-alt tracking-widest text-white/40 uppercase mb-1.5">Add-ons</div>
            <div className="flex flex-wrap gap-1.5">
              {addons.map((a) => (
                <span key={a} className="px-2 py-1 rounded-full bg-white/5 border border-white/10 text-white/70 text-[11px]">{a}</span>
              ))}
            </div>
          </div>
        )}

        {quote.message && (
          <div className="mb-4 p-3 rounded-xl bg-white/5 border border-white/8">
            <div className="text-[9px] font-mono-alt tracking-widest text-white/40 uppercase mb-1">Client message</div>
            <div className="text-white/80 text-sm whitespace-pre-wrap" data-testid="lead-detail-message">{quote.message}</div>
          </div>
        )}

        {comms.length > 0 && (
          <div className="mb-4" data-testid="lead-detail-history">
            <div className="text-[9px] font-mono-alt tracking-widest text-white/40 uppercase mb-1.5">History</div>
            <div className="space-y-1.5 max-h-32 overflow-y-auto pr-1">
              {comms.map((m, i) => (
                <div key={i} className="text-[11px] px-2.5 py-1.5 rounded-lg bg-white/5 border border-white/8 flex items-center gap-2">
                  <span className={`shrink-0 px-1 py-px rounded text-[8px] font-bold tracking-wider ${m.ok ? "bg-emerald-500/15 text-emerald-300" : "bg-red-500/15 text-red-300"}`}>
                    {m.ok ? "SENT" : "FAIL"}
                  </span>
                  <span className="text-white/60 whitespace-nowrap">{(m.at || "").slice(0, 16).replace("T", " ")}</span>
                  <span className="text-white/40 truncate">{m.kind === "auto_ack" ? "Auto-acknowledgement" : (m.subject || m.body)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex flex-wrap justify-between items-center gap-2 mt-2">
          <div className="flex flex-wrap gap-2">
            {onBook && !quote.appointment_id && quote.status !== "done" && quote.status !== "cancelled" && (
              <button data-testid="lead-detail-book" onClick={() => onBook(quote)}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-[#1F5EFF] hover:bg-white hover:text-black text-white transition-colors">
                <CalendarPlus className="w-3.5 h-3.5" /> Book appointment
              </button>
            )}
            {quote.appointment_id && (
              <Link to="/crm/calendar" data-testid="lead-detail-open-appointment"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm border border-[#1F5EFF]/40 text-[#8db2ff] hover:bg-[#1F5EFF]/10 transition-colors">
                <CalendarPlus className="w-3.5 h-3.5" /> Booked — open calendar
              </Link>
            )}
            {quote.status !== "in_progress" && quote.status !== "done" && (
              <button data-testid="lead-detail-in-progress" onClick={() => onSetStatus("in_progress")}
                className="px-4 py-2 rounded-lg text-sm border border-amber-500/40 text-amber-300 hover:bg-amber-500/10 transition-colors">
                In progress
              </button>
            )}
            {quote.status !== "done" && (
              <button data-testid="lead-detail-done" onClick={() => onSetStatus("done")}
                className="px-4 py-2 rounded-lg text-sm border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 transition-colors">
                Done
              </button>
            )}
            {onDelete && !confirmDelete && (
              <button data-testid="lead-detail-delete" onClick={() => setConfirmDelete(true)}
                className="px-4 py-2 rounded-lg text-sm border border-red-500/40 text-red-300 hover:bg-red-500/10 transition-colors">
                Delete
              </button>
            )}
            {onDelete && confirmDelete && (
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-red-500/10 border border-red-500/40" data-testid="lead-detail-delete-confirm-box">
                <span className="text-red-300 text-xs">Delete this request permanently?</span>
                <button data-testid="lead-detail-delete-confirm" onClick={() => onDelete(quote)}
                  className="px-2.5 py-1 rounded bg-red-500/80 hover:bg-red-500 text-white text-xs font-medium">Yes, delete</button>
                <button data-testid="lead-detail-delete-cancel" onClick={() => setConfirmDelete(false)}
                  className="px-2.5 py-1 text-white/50 hover:text-white text-xs">Cancel</button>
              </span>
            )}
          </div>
          <div className="flex flex-wrap justify-end gap-2">
            {quote.customer_id && (
              <Link to={`/crm/customers/${quote.customer_id}`} data-testid="lead-detail-open-customer"
                className="px-4 py-2 rounded-lg text-sm text-white/60 hover:text-white hover:bg-white/5 border border-white/10">
                Open customer
              </Link>
            )}
            {quote.phone && (
              <a href={`tel:${quote.phone}`} data-testid="lead-detail-call"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm text-white/60 hover:text-white hover:bg-white/5 border border-white/10">
                <Phone className="w-3.5 h-3.5" /> Call
              </a>
            )}
            {quote.email && (
              <button data-testid="lead-detail-reply" onClick={() => onReply(quote)}
                className="inline-flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium bg-[#1F5EFF] hover:bg-[#8db2ff] hover:text-black text-white transition-colors">
                <Mail className="w-3.5 h-3.5" /> Reply by email
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
